// src/components/Integrations/BigQueryDetailModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useOrganization2 } from '../../contexts/OrganizationContext2';
import {
  PROVIDERS,
  disableBigQueryOrgIntegration,
  discardBigQueryConnectionDraft,
  discardBigQueryOrgPolicyDraft,
  fetchBigQueryOrgIntegration,
  getBigQueryExportQueueHealth,
  installBigQueryOrgIntegration,
  publishBigQueryConnectionDraft,
  publishBigQueryOrgPolicyDraft,
  repairBigQueryExportQueueRecords,
  saveBigQueryConnectionDraft,
  saveBigQueryOrgPolicyDraft,
  validateBigQueryConnectionDraft,
} from './integrationFunctions';
import {
  getStatusLabel,
  getStatusSeverity,
} from './integrationsRegistry';
import BigQueryFormsTab from './BigQueryFormsTab';

const DEFAULT_LIMIT = 1000;
const DEFAULT_LOCATION = 'US';
const DEFAULT_TABLE_PREFIX = 'utm_smart_manager';
const DEFAULT_EXPORT_SCOPE = 'selected_published_forms';
const DEFAULT_DELIVERY_MODE = 'micro_batch';

const TAB_VALUES = {
  CONNECTION: 'connection',
  POLICY: 'policy',
  FORMS: 'forms',
  DIAGNOSTICS: 'diagnostics',
  ADVANCED_REPAIR: 'advanced_repair',
};

const EXPORT_SCOPE_OPTIONS = [
  {
    value: 'selected_published_forms',
    label: 'Selected published forms',
    helperText:
      'Default. BigQuery remains connected at the organization level, but only explicitly enabled published forms export.',
  },
  {
    value: 'all_published_forms',
    label: 'All published forms',
    helperText:
      'Every published form becomes eligible for export. Use only when org-wide export is intentional.',
  },
  {
    value: 'all_published_forms_except_excluded',
    label: 'All published forms except excluded',
    helperText:
      'Published forms export by default, but individual forms can be explicitly disabled.',
  },
  {
    value: 'disabled',
    label: 'Disabled',
    helperText:
      'Keeps the connection available but prevents BigQuery export from running at the org-policy level.',
  },
];

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString();
  } catch (err) {
    return String(value);
  }
}

function getCount(counts, key) {
  const value = counts?.[key];

  if (!Number.isFinite(Number(value))) {
    return 0;
  }

  return Number(value);
}

function statusChipColor(status) {
  return getStatusSeverity(status || '');
}

function normalizeErrors(errors) {
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors
    .filter((error) => error?.eventId)
    .map((error) => ({
      eventId: error.eventId,
      formId: error.formId || null,
      submissionDocId: error.submissionDocId || null,
      eventType: error.eventType || null,
      lastError: error.lastError || null,
      lastFailedAt: error.lastFailedAt || null,
      updatedAt: error.updatedAt || null,
      attempts: error.attempts || 0,
    }));
}

function getOrganizationDisplayName(organization) {
  return (
    organization?.name ||
    organization?.organizationName ||
    organization?.displayName ||
    organization?.companyName ||
    'Current organization'
  );
}

function unwrapIntegration(result) {
  return (
    result?.integration ||
    result?.orgIntegration ||
    result?.data?.integration ||
    result ||
    null
  );
}

function unwrapHealth(result) {
  return (
    result?.health ||
    result?.data?.health ||
    result ||
    null
  );
}

function getPreferredDestination(integration) {
  return (
    integration?.connectionDraft?.destination ||
    integration?.destination ||
    null
  );
}

function getDestinationLabel(destination) {
  if (!destination?.projectId || !destination?.datasetId) {
    return 'No destination selected';
  }

  return `${destination.projectId}.${destination.datasetId}`;
}

function getValidationStatus(integration) {
  return (
    integration?.connectionDraft?.validation?.status ||
    integration?.validation?.status ||
    'not_validated'
  );
}

function validationChipColor(status) {
  if (status === 'valid') return 'success';
  if (status === 'invalid') return 'error';
  if (status === 'not_validated') return 'default';

  return 'warning';
}

function getPolicyDraft(integration) {
  return (
    integration?.policyDraft ||
    integration?.exportPolicyDraft ||
    integration?.orgPolicyDraft ||
    null
  );
}

function getPublishedPolicy(integration) {
  return (
    integration?.exportPolicy ||
    integration?.policy ||
    integration?.orgPolicy ||
    null
  );
}

function getPreferredPolicy(integration) {
  return getPolicyDraft(integration) || getPublishedPolicy(integration) || null;
}

function normalizePolicy(policy) {
  return {
    defaultExportScope:
      policy?.defaultExportScope ||
      policy?.exportScope ||
      policy?.scope ||
      DEFAULT_EXPORT_SCOPE,

    deliveryMode:
      policy?.deliveryMode ||
      DEFAULT_DELIVERY_MODE,

    includeSubmissionEvents:
      policy?.includeSubmissionEvents !== false,
    includeSubmissionAnswers:
      policy?.includeSubmissionAnswers !== false,
    includeProviderEvents:
      policy?.includeProviderEvents !== false,
    includeAssets:
      policy?.includeAssets !== false,
    includeSubmissionActions:
      policy?.includeSubmissionActions !== false,
    includeProviderOutputs:
      policy?.includeProviderOutputs !== false,
    includeSystemMetadata:
      policy?.includeSystemMetadata !== false,

    // Enterprise safety:
    // Missing dryRun should default to true, not false.
    dryRun: policy?.dryRun === false ? false : true,
  };
}

function buildOrgPolicyDraftPayload({
  basePolicy,
  defaultExportScope,
  dryRun,
}) {
  const normalizedBase = normalizePolicy(basePolicy);

  return {
    ...normalizedBase,
    defaultExportScope: defaultExportScope || DEFAULT_EXPORT_SCOPE,
    deliveryMode: normalizedBase.deliveryMode || DEFAULT_DELIVERY_MODE,
    dryRun: dryRun === false ? false : true,
  };
}

function parseEventIds(value) {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      String(value)
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function MetricCard({
  label,
  value,
  helperText,
  severity = 'default',
}) {
  const color =
    severity === 'error'
      ? 'error.main'
      : severity === 'warning'
      ? 'warning.main'
      : severity === 'success'
      ? 'success.main'
      : 'text.primary';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>

        <Typography variant="h5" fontWeight={700} color={color}>
          {value}
        </Typography>

        {helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function KeyValueList({
  title,
  values,
}) {
  const entries = Object.entries(values || {});

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No records found.
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {entries.map(([key, value]) => (
            <Stack
              key={key}
              direction="row"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography variant="body2" color="text.secondary">
                {key}
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function SectionCard({
  title,
  description,
  action,
  children,
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>

            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          {action && <Box>{action}</Box>}
        </Stack>

        {children}
      </Stack>
    </Paper>
  );
}

function StatusHeader({
  integrationStatus,
  validationStatus,
  organizationName,
  organizationId,
  providerId,
  activeDestination,
  policy,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.default',
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={getStatusLabel(integrationStatus)}
                color={statusChipColor(integrationStatus)}
                variant={integrationStatus ? 'filled' : 'outlined'}
              />

              <Chip
                label={`Validation: ${validationStatus}`}
                color={validationChipColor(validationStatus)}
                variant="outlined"
              />

              {policy?.dryRun && (
                <Chip
                  label="Dry run"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary">
              BigQuery status, validation, and export policy are controlled by
              backend lifecycle functions.
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Organization
            </Typography>

            <Typography variant="body2" fontWeight={700}>
              {organizationName}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ wordBreak: 'break-all' }}
            >
              Org ID diagnostic: {organizationId || '—'}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} md={3}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Provider / destination
            </Typography>

            <Typography variant="body2" fontWeight={700}>
              {providerId}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ wordBreak: 'break-all' }}
            >
              {getDestinationLabel(activeDestination)}
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

function TabPanel({
  children,
  tabValue,
  value,
}) {
  if (value !== tabValue) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      id={`bigquery-tabpanel-${tabValue}`}
      aria-labelledby={`bigquery-tab-${tabValue}`}
      sx={{ pt: 2 }}
    >
      {children}
    </Box>
  );
}

export default function BigQueryDetailModal({
  open,
  onClose,
  onChanged,
  availableForms = [],
  formsLoading = false,
  formsError = null,
  hasMoreForms = false,
  loadingMoreForms = false,
  onLoadMoreForms,
  onRefreshForms,
}) {
  const { organization } = useOrganization2();

  const [activeTab, setActiveTab] = useState(TAB_VALUES.CONNECTION);
  const [integration, setIntegration] = useState(null);
  const [health, setHealth] = useState(null);
  const [formFilter, setFormFilter] = useState('');
  const [submissionFilter, setSubmissionFilter] = useState('');

  const [destinationProjectId, setDestinationProjectId] = useState('');
  const [destinationDatasetId, setDestinationDatasetId] = useState('');
  const [destinationLocation, setDestinationLocation] = useState(DEFAULT_LOCATION);
  const [destinationTablePrefix, setDestinationTablePrefix] =
    useState(DEFAULT_TABLE_PREFIX);

  const [policyExportScope, setPolicyExportScope] =
    useState(DEFAULT_EXPORT_SCOPE);
  const [policyDryRun, setPolicyDryRun] = useState(true);

  const [loading, setLoading] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [validatingConnection, setValidatingConnection] = useState(false);
  const [publishingConnection, setPublishingConnection] = useState(false);
  const [discardingConnection, setDiscardingConnection] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [disableReason, setDisableReason] = useState('');

  const [savingPolicy, setSavingPolicy] = useState(false);
  const [publishingPolicy, setPublishingPolicy] = useState(false);
  const [discardingPolicy, setDiscardingPolicy] = useState(false);

  const [repairing, setRepairing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [repairMsg, setRepairMsg] = useState('');
  const [repairReason, setRepairReason] = useState('');
  const [verifiedWarehouseRows, setVerifiedWarehouseRows] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [repairEventIdsInput, setRepairEventIdsInput] = useState('');

  const organizationId = organization?.id || organization?.organizationId || null;
  const organizationName = getOrganizationDisplayName(organization);

  const activeDestination = integration?.destination || null;
  const draftDestination = integration?.connectionDraft?.destination || null;
  const validationStatus = getValidationStatus(integration);
  const hasConnectionDraft = Boolean(draftDestination);
  const hasValidatedDraft =
    integration?.connectionDraft?.validation?.status === 'valid';

  const integrationStatus = integration?.status || '';
  const isSetupInitialized =
    Boolean(integration) && integration?.status !== 'not_installed';

  const isBigQueryConnected =
    integration?.isConnected === true ||
    integration?.status === 'connected' ||
    integration?.status === 'active';

  const writerEmail = integration?.writerIdentity?.email || '';

  const policyDraft = getPolicyDraft(integration);
  const publishedPolicy = getPublishedPolicy(integration);
  const preferredPolicy = useMemo(() => {
    return normalizePolicy(getPreferredPolicy(integration));
  }, [integration]);

  const hasPolicyDraft = Boolean(policyDraft);
  const policyBusy = savingPolicy || publishingPolicy || discardingPolicy || loading;

  const connectionBusy =
    loading ||
    savingConnection ||
    validatingConnection ||
    publishingConnection ||
    discardingConnection ||
    disconnecting;

  const canSaveConnectionDraft =
    Boolean(destinationProjectId.trim()) &&
    Boolean(destinationDatasetId.trim()) &&
    !connectionBusy;

  const canValidateConnectionDraft =
    hasConnectionDraft &&
    !connectionBusy;

  const canPublishConnectionDraft =
    hasValidatedDraft &&
    !connectionBusy;

  const canSavePolicyDraft =
    isSetupInitialized &&
    Boolean(policyExportScope) &&
    !policyBusy;

  const canPublishPolicyDraft =
    hasPolicyDraft &&
    !policyBusy;

  const recentErrors = useMemo(() => {
    return normalizeErrors(health?.recentErrors);
  }, [health]);

  const countsByStatus = health?.countsByStatus || {};
  const countsByDeliveryMode = health?.countsByDeliveryMode || {};
  const countsByEventType = health?.countsByEventType || {};

  const deliveredCount = getCount(countsByStatus, 'delivered');
  const queuedCount = getCount(countsByStatus, 'queued');
  const processingCount = getCount(countsByStatus, 'processing');
  const errorCount = getCount(countsByStatus, 'error');

  const selectedErrorCount = selectedEventIds.length;
  const repairEventIds = useMemo(() => {
    return parseEventIds(repairEventIdsInput);
  }, [repairEventIdsInput]);

  const allErrorsSelected = recentErrors.length > 0 &&
    recentErrors.every((item) => selectedEventIds.includes(item.eventId));

  const canRepair = repairEventIds.length > 0 &&
    repairReason.trim() &&
    verifiedWarehouseRows &&
    !repairing;

  const syncDestinationFormFromIntegration = useCallback((nextIntegration) => {
    const destination = getPreferredDestination(nextIntegration);

    if (!destination) {
      setDestinationProjectId('');
      setDestinationDatasetId('');
      setDestinationLocation(DEFAULT_LOCATION);
      setDestinationTablePrefix(DEFAULT_TABLE_PREFIX);
      return;
    }

    setDestinationProjectId(destination.projectId || '');
    setDestinationDatasetId(destination.datasetId || '');
    setDestinationLocation(destination.location || DEFAULT_LOCATION);
    setDestinationTablePrefix(
      destination.tablePrefix || DEFAULT_TABLE_PREFIX
    );
  }, []);

  const syncPolicyFormFromIntegration = useCallback((nextIntegration) => {
    const nextPolicy = normalizePolicy(getPreferredPolicy(nextIntegration));

    setPolicyExportScope(
      nextPolicy.defaultExportScope || DEFAULT_EXPORT_SCOPE
    );
    setPolicyDryRun(nextPolicy.dryRun === false ? false : true);
  }, []);


  const loadBigQueryState = useCallback(async ({
    clearMessages = true,
    formId = null,
    submissionDocId = null,
  } = {}) => {
    if (!open) {
      return;
    }

    if (clearMessages) {
      setErrorMsg('');
      setActionMsg('');
      setRepairMsg('');
    }

    if (!organizationId) {
      setIntegration(null);
      setHealth(null);
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setLoading(true);

      const [integrationResult, healthResult] = await Promise.all([
        fetchBigQueryOrgIntegration({
          organizationId,
        }),
        getBigQueryExportQueueHealth({
          organizationId,
          formId,
          submissionDocId,
          limit: DEFAULT_LIMIT,
        }),
      ]);

      const nextIntegration = unwrapIntegration(integrationResult);
      const nextHealth = unwrapHealth(healthResult);

      setIntegration(nextIntegration || null);
      setHealth(nextHealth || null);
      setSelectedEventIds([]);

      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);

      console.log('[BigQueryDetailModal] BigQuery state loaded:', {
        organizationId,
        organizationName,
        filters: {
          formId,
          submissionDocId,
          limit: DEFAULT_LIMIT,
        },
        integration: nextIntegration,
        health: nextHealth || null,
      });
    } catch (err) {
      console.error('[BigQueryDetailModal] Failed to load BigQuery state:', err);
      setErrorMsg(
        err?.message ||
          'Failed to load BigQuery integration details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    open,
    organizationId,
    organizationName,
    syncDestinationFormFromIntegration,
    syncPolicyFormFromIntegration,
  ]);

  const handleRefresh = useCallback(() => {
    loadBigQueryState({
      clearMessages: true,
      formId: formFilter.trim() || null,
      submissionDocId: submissionFilter.trim() || null,
    });
  }, [formFilter, loadBigQueryState, submissionFilter]);

  useEffect(() => {
    if (open) {
      setActiveTab(TAB_VALUES.CONNECTION);
      setDisableConfirmOpen(false);
      setDisableReason('');
      loadBigQueryState({
        clearMessages: true,
        formId: null,
        submissionDocId: null,
      });
    }
  }, [loadBigQueryState, open]);


  const handleInstallBigQuery = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setSavingConnection(true);

      const result = await installBigQueryOrgIntegration({
        organizationId,
      });

      const nextIntegration = unwrapIntegration(result);

      setIntegration(nextIntegration || null);
      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);
      setActionMsg(
        'BigQuery setup initialized. Add your customer-owned destination ' +
          'details, grant dataset access to the writer identity, then validate.'
      );

      console.log('[BigQueryDetailModal] BigQuery setup initialized:', {
        organizationId,
        organizationName,
        integration: nextIntegration,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] BigQuery setup failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to initialize BigQuery setup. Please try again.'
      );
    } finally {
      setSavingConnection(false);
    }
  };

  const handleSaveConnectionDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    if (!canSaveConnectionDraft) {
      setErrorMsg(
        'Enter a BigQuery project ID and dataset ID before saving the draft.'
      );
      return;
    }

    try {
      setSavingConnection(true);

      const result = await saveBigQueryConnectionDraft({
        organizationId,
        destination: {
          projectId: destinationProjectId.trim(),
          datasetId: destinationDatasetId.trim(),
          location: destinationLocation.trim() || DEFAULT_LOCATION,
          tablePrefix: destinationTablePrefix.trim() || DEFAULT_TABLE_PREFIX,
        },
      });

      const nextIntegration = unwrapIntegration(result);

      setIntegration(nextIntegration || null);
      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);
      setActionMsg(
        'BigQuery destination draft saved. Grant dataset access to the ' +
          'writer identity, then validate the connection.'
      );

      console.log('[BigQueryDetailModal] Connection draft saved:', {
        organizationId,
        organizationName,
        destinationProjectId,
        destinationDatasetId,
        destinationLocation,
        destinationTablePrefix,
        integration: nextIntegration,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Save connection draft failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to save the BigQuery connection draft.'
      );
    } finally {
      setSavingConnection(false);
    }
  };

  const handleValidateConnectionDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setValidatingConnection(true);

      const result = await validateBigQueryConnectionDraft({
        organizationId,
      });

      const nextIntegration = unwrapIntegration(result);

      setIntegration(nextIntegration || null);
      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);

      if (result?.ok === true) {
        setActionMsg(
          'BigQuery destination validated successfully. You can now publish ' +
            'the connection.'
        );
      } else {
        setErrorMsg(
          result?.validation?.error?.message ||
            nextIntegration?.connectionDraft?.validation?.error?.message ||
            'BigQuery destination validation failed. Review IAM and dataset ' +
              'settings, then try again.'
        );
      }

      console.warn('[BigQueryDetailModal] Connection validation completed:', {
        organizationId,
        organizationName,
        result,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Validate connection failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to validate the BigQuery destination.'
      );
    } finally {
      setValidatingConnection(false);
    }
  };

  const handlePublishConnectionDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setPublishingConnection(true);

      const result = await publishBigQueryConnectionDraft({
        organizationId,
      });

      const nextIntegration = unwrapIntegration(result);

      setIntegration(nextIntegration || null);
      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);
      setActionMsg(
        'BigQuery connection published. Exports remain governed by your org ' +
          'policy, form-level settings, and dry-run configuration.'
      );

      console.warn('[BigQueryDetailModal] Connection draft published:', {
        organizationId,
        organizationName,
        integration: nextIntegration,
      });

      await loadBigQueryState({
        clearMessages: false,
        formId: formFilter.trim() || null,
        submissionDocId: submissionFilter.trim() || null,
      });
      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Publish connection failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to publish the BigQuery connection draft.'
      );
    } finally {
      setPublishingConnection(false);
    }
  };

  const handleDiscardConnectionDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setDiscardingConnection(true);

      const result = await discardBigQueryConnectionDraft({
        organizationId,
      });

      const nextIntegration = unwrapIntegration(result);

      setIntegration(nextIntegration || null);
      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);
      setActionMsg('BigQuery connection draft discarded.');

      console.log('[BigQueryDetailModal] Connection draft discarded:', {
        organizationId,
        organizationName,
        integration: nextIntegration,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Discard connection draft failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to discard the BigQuery connection draft.'
      );
    } finally {
      setDiscardingConnection(false);
    }
  };

  const handleDisableBigQuery = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setDisconnecting(true);

      const result = await disableBigQueryOrgIntegration({
        organizationId,
        reason:
          disableReason.trim() ||
          'Disabled from BigQuery integration modal.',
      });

      const nextIntegration = unwrapIntegration(result);

      setIntegration(nextIntegration || null);
      syncDestinationFormFromIntegration(nextIntegration || null);
      syncPolicyFormFromIntegration(nextIntegration || null);
      setDisableConfirmOpen(false);
      setDisableReason('');
      setActionMsg(
        'BigQuery export has been disconnected. Historical exports, queue ' +
          'records, and customer BigQuery tables were not deleted.'
      );

      console.warn('[BigQueryDetailModal] BigQuery disconnected:', {
        organizationId,
        organizationName,
        integration: nextIntegration,
      });

      await loadBigQueryState({
        clearMessages: false,
        formId: formFilter.trim() || null,
        submissionDocId: submissionFilter.trim() || null,
      });
      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Disable BigQuery failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to disconnect BigQuery.'
      );
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCopyWriterIdentity = async () => {
    if (!writerEmail) {
      return;
    }

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable.');
      }

      await navigator.clipboard.writeText(writerEmail);
      setActionMsg('Writer service account copied to clipboard.');
    } catch (err) {
      console.error('[BigQueryDetailModal] Copy writer identity failed:', err);
      setErrorMsg(
        'Could not copy the writer identity. Copy it manually instead.'
      );
    }
  };

  const handleSavePolicyDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    if (!canSavePolicyDraft) {
      setErrorMsg('Initialize BigQuery setup before saving an export policy.');
      return;
    }

    try {
      setSavingPolicy(true);

      const policyPayload = buildOrgPolicyDraftPayload({
        basePolicy: getPreferredPolicy(integration),
        defaultExportScope: policyExportScope,
        dryRun: policyDryRun,
      });

      const result = await saveBigQueryOrgPolicyDraft({
        organizationId,
        policy: policyPayload,
      });

      const nextIntegration = unwrapIntegration(result);

      if (nextIntegration) {
        setIntegration(nextIntegration);
        syncPolicyFormFromIntegration(nextIntegration);
      } else {
        await loadBigQueryState({
          clearMessages: false,
          formId: formFilter.trim() || null,
          submissionDocId: submissionFilter.trim() || null,
        });
      }

      setActionMsg(
        'BigQuery export policy draft saved. Publish the policy when you are ' +
          'ready for it to govern exports.'
      );

      console.log('[BigQueryDetailModal] BigQuery policy draft saved:', {
        organizationId,
        organizationName,
        policy: policyPayload,
        integration: nextIntegration,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Save policy draft failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to save the BigQuery export policy draft.'
      );
    } finally {
      setSavingPolicy(false);
    }
  };

  const handlePublishPolicyDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setPublishingPolicy(true);

      const result = await publishBigQueryOrgPolicyDraft({
        organizationId,
      });

      const nextIntegration = unwrapIntegration(result);

      if (nextIntegration) {
        setIntegration(nextIntegration);
        syncPolicyFormFromIntegration(nextIntegration);
      } else {
        await loadBigQueryState({
          clearMessages: false,
          formId: formFilter.trim() || null,
          submissionDocId: submissionFilter.trim() || null,
        });
      }

      setActionMsg(
        'BigQuery export policy published. Future exports will follow the ' +
          'published org-level policy.'
      );

      console.warn('[BigQueryDetailModal] BigQuery policy draft published:', {
        organizationId,
        organizationName,
        integration: nextIntegration,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Publish policy draft failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to publish the BigQuery export policy draft.'
      );
    } finally {
      setPublishingPolicy(false);
    }
  };

  const handleDiscardPolicyDraft = async () => {
    setErrorMsg('');
    setActionMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    try {
      setDiscardingPolicy(true);

      const result = await discardBigQueryOrgPolicyDraft({
        organizationId,
      });

      const nextIntegration = unwrapIntegration(result);

      if (nextIntegration) {
        setIntegration(nextIntegration);
        syncPolicyFormFromIntegration(nextIntegration);
      } else {
        await loadBigQueryState({
          clearMessages: false,
          formId: formFilter.trim() || null,
          submissionDocId: submissionFilter.trim() || null,
        });
      }

      setActionMsg('BigQuery export policy draft discarded.');

      console.log('[BigQueryDetailModal] BigQuery policy draft discarded:', {
        organizationId,
        organizationName,
        integration: nextIntegration,
      });

      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Discard policy draft failed:', err);
      setErrorMsg(
        err?.message ||
          'Failed to discard the BigQuery export policy draft.'
      );
    } finally {
      setDiscardingPolicy(false);
    }
  };

  const handleToggleEventId = (eventId, checked) => {
    setSelectedEventIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, eventId]));
      }

      return prev.filter((item) => item !== eventId);
    });
  };

  const handleToggleAllErrors = (checked) => {
    if (!checked) {
      setSelectedEventIds([]);
      return;
    }

    setSelectedEventIds(recentErrors.map((item) => item.eventId));
  };

  const handleUseSelectedErrorsForRepair = () => {
    setRepairEventIdsInput(selectedEventIds.join('\n'));
    setActiveTab(TAB_VALUES.ADVANCED_REPAIR);
  };

  const handleRepairRecords = async () => {
    setErrorMsg('');
    setRepairMsg('');

    if (!organizationId) {
      setErrorMsg('No organization found for this session.');
      return;
    }

    if (!canRepair) {
      setErrorMsg(
        'Enter explicit event IDs, provide a repair reason, and confirm that ' +
          'warehouse rows were verified.'
      );
      return;
    }

    try {
      setRepairing(true);

      const result = await repairBigQueryExportQueueRecords({
        organizationId,
        eventIds: repairEventIds,
        repairReason: repairReason.trim(),
        verifiedWarehouseRows,
      });

      console.warn('[BigQueryDetailModal] Repair completed:', {
        organizationId,
        organizationName,
        eventIds: repairEventIds,
        result,
      });

      setRepairMsg(
        `Repair completed. Repaired: ` +
          `${result?.result?.repairedCount ?? result?.repairedCount ?? 0}. ` +
          `Skipped: ${result?.result?.skippedCount ?? result?.skippedCount ?? 0}.`
      );

      setRepairReason('');
      setVerifiedWarehouseRows(false);
      setSelectedEventIds([]);
      setRepairEventIdsInput('');

      await loadBigQueryState({
        clearMessages: false,
        formId: formFilter.trim() || null,
        submissionDocId: submissionFilter.trim() || null,
      });
      onChanged?.();
    } catch (err) {
      console.error('[BigQueryDetailModal] Repair failed:', err);
      setErrorMsg(
        err?.message ||
          'Repair failed. Review the queue records and try again.'
      );
    } finally {
      setRepairing(false);
    }
  };

  const selectedExportScopeOption = EXPORT_SCOPE_OPTIONS.find(
    (option) => option.value === policyExportScope
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '92vh',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <StorageIcon color="primary" />

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              BigQuery Integration
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Customer-owned warehouse destination for governed form exports.
            </Typography>
          </Box>

          <Tooltip title="Refresh BigQuery state">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                aria-label="Refresh BigQuery integration state"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {errorMsg && (
            <Alert severity="error" onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}

          {actionMsg && (
            <Alert severity="success" onClose={() => setActionMsg('')}>
              {actionMsg}
            </Alert>
          )}

          {repairMsg && (
            <Alert severity="success" onClose={() => setRepairMsg('')}>
              {repairMsg}
            </Alert>
          )}

          <StatusHeader
            integrationStatus={integrationStatus}
            validationStatus={validationStatus}
            organizationName={organizationName}
            organizationId={organizationId}
            providerId={PROVIDERS.BIGQUERY}
            activeDestination={activeDestination}
            policy={preferredPolicy}
          />

          <Alert severity="info">
            BigQuery uses a customer-owned destination model. UTM Smart Manager
            never stores customer service account keys and the browser never
            defines warehouse schema. Setup is saved, validated, and published
            through backend lifecycle functions.
          </Alert>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="BigQuery integration sections"
            >
              <Tab
                id={`bigquery-tab-${TAB_VALUES.CONNECTION}`}
                value={TAB_VALUES.CONNECTION}
                label="Connection"
              />
              <Tab
                id={`bigquery-tab-${TAB_VALUES.POLICY}`}
                value={TAB_VALUES.POLICY}
                label="Export policy"
              />
              <Tab
                id={`bigquery-tab-${TAB_VALUES.FORMS}`}
                value={TAB_VALUES.FORMS}
                label="Forms"
              />
              <Tab
                id={`bigquery-tab-${TAB_VALUES.DIAGNOSTICS}`}
                value={TAB_VALUES.DIAGNOSTICS}
                label="Diagnostics"
              />
              <Tab
                id={`bigquery-tab-${TAB_VALUES.ADVANCED_REPAIR}`}
                value={TAB_VALUES.ADVANCED_REPAIR}
                label="Advanced repair"
              />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} tabValue={TAB_VALUES.CONNECTION}>
            <Stack spacing={2}>
              <SectionCard
                title="Connection"
                description={
                  'Connect an organization-owned BigQuery dataset. Reconnecting is handled by saving, validating, and publishing a new destination draft.'
                }
                action={
                  !isSetupInitialized ? (
                    <Button
                      variant="contained"
                      onClick={handleInstallBigQuery}
                      disabled={connectionBusy}
                    >
                      {savingConnection ? 'Initializing...' : 'Enable BigQuery setup'}
                    </Button>
                  ) : !isBigQueryConnected ? (
                    <Button
                      variant="contained"
                      onClick={handleInstallBigQuery}
                      disabled={connectionBusy}
                    >
                      {savingConnection ? 'Continuing...' : 'Continue setup'}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setDisableConfirmOpen((prev) => !prev)}
                      disabled={connectionBusy}
                    >
                      Disconnect
                    </Button>
                  )
                }
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Writer service account
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        {writerEmail || 'Available after setup is initialized.'}
                      </Typography>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={handleCopyWriterIdentity}
                        disabled={!writerEmail}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Copy writer identity
                      </Button>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Active destination
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {getDestinationLabel(activeDestination)}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Connection mode:{' '}
                        {integration?.connectionMode || 'customer_project'}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>

                <Alert severity="info">
                  Grant the writer service account dataset-level BigQuery Data
                  Editor access on the selected dataset. Validation creates and
                  removes a temporary validation table before the destination
                  can be published.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Google Cloud project ID"
                      value={destinationProjectId}
                      onChange={(event) => {
                        setDestinationProjectId(event.target.value);
                      }}
                      fullWidth
                      size="small"
                      placeholder="customer-project-id"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Dataset ID"
                      value={destinationDatasetId}
                      onChange={(event) => {
                        setDestinationDatasetId(event.target.value);
                      }}
                      fullWidth
                      size="small"
                      placeholder="marketing_ops"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Location"
                      value={destinationLocation}
                      onChange={(event) => {
                        setDestinationLocation(event.target.value);
                      }}
                      fullWidth
                      size="small"
                      placeholder="US"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Table prefix"
                      value={destinationTablePrefix}
                      onChange={(event) => {
                        setDestinationTablePrefix(event.target.value);
                      }}
                      fullWidth
                      size="small"
                      placeholder="utm_smart_manager"
                    />
                  </Grid>
                </Grid>

                {hasConnectionDraft && (
                  <Alert severity={hasValidatedDraft ? 'success' : 'warning'}>
                    Draft destination: {getDestinationLabel(draftDestination)}.
                    Validation status:{' '}
                    {integration?.connectionDraft?.validation?.status ||
                      'not_validated'}
                  </Alert>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Button
                    variant="outlined"
                    onClick={handleSaveConnectionDraft}
                    disabled={!canSaveConnectionDraft}
                  >
                    {savingConnection ? 'Saving...' : 'Save draft'}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleValidateConnectionDraft}
                    disabled={!canValidateConnectionDraft}
                  >
                    {validatingConnection ? 'Validating...' : 'Validate'}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handlePublishConnectionDraft}
                    disabled={!canPublishConnectionDraft}
                  >
                    {publishingConnection ? 'Publishing...' : 'Publish'}
                  </Button>

                  <Button
                    variant="text"
                    color="warning"
                    onClick={handleDiscardConnectionDraft}
                    disabled={!hasConnectionDraft || connectionBusy}
                  >
                    {discardingConnection ? 'Discarding...' : 'Discard draft'}
                  </Button>
                </Stack>
              </SectionCard>

              {disableConfirmOpen && (
                <SectionCard
                  title="Confirm disconnect"
                  description="Disconnecting stops future BigQuery exports without deleting UTM Smart Manager submissions, queue history, customer BigQuery tables, or exported rows."
                >
                  <Alert severity="warning">
                    Use this only when the organization intentionally wants to
                    stop future BigQuery delivery. To reconnect later, save,
                    validate, and publish a new destination draft.
                  </Alert>

                  <TextField
                    label="Reason"
                    value={disableReason}
                    onChange={(event) => setDisableReason(event.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Optional reason for audit history"
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      onClick={() => {
                        setDisableConfirmOpen(false);
                        setDisableReason('');
                      }}
                      disabled={disconnecting}
                    >
                      Cancel
                    </Button>

                    <Button
                      color="error"
                      variant="contained"
                      onClick={handleDisableBigQuery}
                      disabled={disconnecting}
                    >
                      {disconnecting ? 'Disconnecting...' : 'Disconnect BigQuery'}
                    </Button>
                  </Stack>
                </SectionCard>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={activeTab} tabValue={TAB_VALUES.POLICY}>
            <Stack spacing={2}>
              <SectionCard
                title="Org-level export policy"
                description="The policy determines which forms are eligible for BigQuery export after the organization connection is published."
              >
                <Alert severity="info">
                  The enterprise-safe default is selected published forms. This
                  prevents a newly connected warehouse from automatically
                  receiving every form submission unless an admin intentionally
                  changes the org policy.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Export scope"
                      value={policyExportScope}
                      onChange={(event) => {
                        setPolicyExportScope(event.target.value);
                      }}
                      fullWidth
                      size="small"
                    >
                      {EXPORT_SCOPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current selection
                      </Typography>

                      <Typography variant="body2" fontWeight={700}>
                        {selectedExportScopeOption?.label || policyExportScope}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {selectedExportScopeOption?.helperText ||
                          'No helper text available for this export scope.'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={policyDryRun}
                      onChange={(event) => {
                        setPolicyDryRun(event.target.checked);
                      }}
                    />
                  }
                  label="Dry run mode"
                />

                <Alert severity={policyDryRun ? 'warning' : 'success'}>
                  {policyDryRun
                    ? 'Dry run is enabled in the policy draft. Queue and delivery behavior can be tested without writing export rows to BigQuery.'
                    : 'Dry run is off. Published eligible exports will write to BigQuery when the connection, policy, and form settings allow delivery.'}
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <KeyValueList
                      title="Published policy"
                      values={{
                        defaultExportScope:
                          normalizePolicy(publishedPolicy).defaultExportScope,
                        deliveryMode:
                          normalizePolicy(publishedPolicy).deliveryMode,
                        dryRun: String(normalizePolicy(publishedPolicy).dryRun),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <KeyValueList
                      title="Policy draft"
                      values={
                        hasPolicyDraft
                          ? {
                              defaultExportScope:
                                normalizePolicy(policyDraft).defaultExportScope,
                              deliveryMode:
                                normalizePolicy(policyDraft).deliveryMode,
                              dryRun: String(normalizePolicy(policyDraft).dryRun),
                            }
                          : {}
                      }
                    />
                  </Grid>
                </Grid>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Button
                    variant="outlined"
                    onClick={handleSavePolicyDraft}
                    disabled={!canSavePolicyDraft}
                  >
                    {savingPolicy ? 'Saving...' : 'Save policy draft'}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handlePublishPolicyDraft}
                    disabled={!canPublishPolicyDraft}
                  >
                    {publishingPolicy ? 'Publishing...' : 'Publish policy'}
                  </Button>

                  <Button
                    variant="text"
                    color="warning"
                    onClick={handleDiscardPolicyDraft}
                    disabled={!hasPolicyDraft || policyBusy}
                  >
                    {discardingPolicy ? 'Discarding...' : 'Discard policy draft'}
                  </Button>
                </Stack>
              </SectionCard>
            </Stack>
          </TabPanel>

          <TabPanel value={activeTab} tabValue={TAB_VALUES.FORMS}>
            <BigQueryFormsTab
              organizationId={organizationId}
              isBigQueryConnected={isBigQueryConnected}
              exportScope={normalizePolicy(publishedPolicy).defaultExportScope}
              policyDryRun={normalizePolicy(publishedPolicy).dryRun}
              forms={availableForms}
              formsLoading={formsLoading}
              formsError={formsError}
              hasMoreForms={hasMoreForms}
              loadingMoreForms={loadingMoreForms}
              onLoadMoreForms={onLoadMoreForms}
              onRefreshForms={onRefreshForms}
              onChanged={onChanged}
            />
          </TabPanel>

          <TabPanel value={activeTab} tabValue={TAB_VALUES.DIAGNOSTICS}>
            <Stack spacing={2}>
              <SectionCard
                title="Queue diagnostics"
                description="Inspect organization-level BigQuery queue health. Filters are optional and should be used only when investigating a specific form or submission."
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Form ID filter"
                      value={formFilter}
                      onChange={(event) => setFormFilter(event.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Optional"
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Submission ID filter"
                      value={submissionFilter}
                      onChange={(event) => {
                        setSubmissionFilter(event.target.value);
                      }}
                      fullWidth
                      size="small"
                      placeholder="Optional"
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      onClick={handleRefresh}
                      disabled={loading}
                      variant="outlined"
                      fullWidth
                      sx={{ height: '40px' }}
                    >
                      Apply
                    </Button>
                  </Grid>
                </Grid>

                {loading ? (
                  <Stack
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 5 }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading BigQuery diagnostics...
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                          label="Delivered"
                          value={deliveredCount}
                          severity="success"
                          helperText="Records marked delivered."
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                          label="Queued"
                          value={queuedCount}
                          severity={queuedCount > 0 ? 'warning' : 'default'}
                          helperText="Waiting for micro-batch flush."
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                          label="Processing"
                          value={processingCount}
                          severity={processingCount > 0 ? 'warning' : 'default'}
                          helperText="Currently claimed by writer."
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                          label="Errors"
                          value={errorCount}
                          severity={errorCount > 0 ? 'error' : 'default'}
                          helperText="Requires investigation."
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <KeyValueList
                          title="Delivery modes"
                          values={countsByDeliveryMode}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <KeyValueList
                          title="Event types"
                          values={countsByEventType}
                        />
                      </Grid>
                    </Grid>

                    <Stack spacing={1}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Recent errors
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Select records only after investigation. Repair is
                            handled in the Advanced repair tab and does not
                            re-export rows.
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          color="warning"
                          disabled={selectedErrorCount === 0}
                          onClick={handleUseSelectedErrorsForRepair}
                        >
                          Use selected for repair
                        </Button>
                      </Stack>

                      {recentErrors.length === 0 ? (
                        <Alert severity="success">
                          No recent BigQuery queue errors were found for this scope.
                        </Alert>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={allErrorsSelected}
                                    indeterminate={
                                      selectedEventIds.length > 0 &&
                                      !allErrorsSelected
                                    }
                                    onChange={(event) => {
                                      handleToggleAllErrors(event.target.checked);
                                    }}
                                  />
                                </TableCell>
                                <TableCell>Event ID</TableCell>
                                <TableCell>Form</TableCell>
                                <TableCell>Submission</TableCell>
                                <TableCell>Event type</TableCell>
                                <TableCell>Attempts</TableCell>
                                <TableCell>Failed</TableCell>
                                <TableCell>Error</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {recentErrors.map((error) => (
                                <TableRow key={error.eventId} hover>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectedEventIds.includes(
                                        error.eventId
                                      )}
                                      onChange={(event) => {
                                        handleToggleEventId(
                                          error.eventId,
                                          event.target.checked
                                        );
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: 'monospace',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      {error.eventId}
                                    </Typography>
                                  </TableCell>

                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {error.formId || '—'}
                                    </Typography>
                                  </TableCell>

                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {error.submissionDocId || '—'}
                                    </Typography>
                                  </TableCell>

                                  <TableCell>{error.eventType || '—'}</TableCell>
                                  <TableCell>{error.attempts}</TableCell>
                                  <TableCell>
                                    {formatDateTime(
                                      error.lastFailedAt || error.updatedAt
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ wordBreak: 'break-word' }}
                                    >
                                      {error.lastError || 'Unknown error'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Stack>

                    {health?.generatedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Diagnostics generated at {formatDateTime(health.generatedAt)}
                      </Typography>
                    )}
                  </Stack>
                )}
              </SectionCard>
            </Stack>
          </TabPanel>

          <TabPanel value={activeTab} tabValue={TAB_VALUES.ADVANCED_REPAIR}>
            <Stack spacing={2}>
              <SectionCard
                title="Advanced repair"
                description="Repair is guarded and should only be used for verified historical delivery-state corrections."
              >
                <Alert severity="warning" icon={<WarningAmberIcon />}>
                  Repair does not re-export records to BigQuery. It only repairs
                  Firestore delivery state for explicit queue event IDs after
                  the corresponding warehouse rows have been independently
                  verified.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      label="Explicit event IDs"
                      value={repairEventIdsInput}
                      onChange={(event) => {
                        setRepairEventIdsInput(event.target.value);
                      }}
                      fullWidth
                      multiline
                      minRows={4}
                      placeholder="Paste one or more queue event IDs, separated by line breaks, spaces, or commas."
                      helperText={`${repairEventIds.length} event ID(s) will be submitted for repair.`}
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Selected recent errors
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {selectedErrorCount} selected from diagnostics.
                        </Typography>

                        <Button
                          variant="outlined"
                          size="small"
                          disabled={selectedErrorCount === 0}
                          onClick={handleUseSelectedErrorsForRepair}
                        >
                          Copy selected event IDs
                        </Button>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>

                <TextField
                  label="Repair reason"
                  value={repairReason}
                  onChange={(event) => {
                    setRepairReason(event.target.value);
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={
                    'Example: Verified rows already exist in BigQuery from pre-patch writer metadata bug.'
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={verifiedWarehouseRows}
                      onChange={(event) => {
                        setVerifiedWarehouseRows(event.target.checked);
                      }}
                    />
                  }
                  label={
                    'I verified that the corresponding warehouse rows already exist and should not be re-exported.'
                  }
                />

                <Button
                  variant="contained"
                  color="warning"
                  disabled={!canRepair}
                  onClick={handleRepairRecords}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {repairing ? 'Repairing...' : 'Repair queue records'}
                </Button>
              </SectionCard>
            </Stack>
          </TabPanel>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
